import React from 'react';
import {
  Sparkles, Video, MessageSquareQuote, Tags, Search, Headphones,
  BookOpen, Database, Cpu, Wand2, Rocket, FlaskConical,
} from 'lucide-react';
import './AboutPage.css';

const FEATURES = [
  {
    icon: MessageSquareQuote,
    title: 'Kommentar',
    text: 'KI-synthetisierte Lehraussagen aus allen Videos zu einem Vers — kategorisiert nach Textanalyse, Theologie, Christologie, Anwendung und Illustration.',
  },
  {
    icon: BookOpen,
    title: 'Querverweise',
    text: 'Bibelstellen, die Prediger im Zusammenhang mit dem Vers zitieren — automatisch aus den Videotranskripten extrahiert.',
  },
  {
    icon: Tags,
    title: 'Themen',
    text: 'Übergreifende biblische Themen (Schöpfung, Gnade, Erlösung …) mit Verlinkung zu allen relevanten Versen.',
  },
  {
    icon: Video,
    title: 'Video-Clips',
    text: 'Jede Verserwähnung wird als eigenständiger Clip aufbereitet — mit KI-generiertem Titel, Beschreibung und präzisem Start-/Endpunkt.',
  },
  {
    icon: Sparkles,
    title: 'KI-Einblicke',
    text: 'Ein RAG-Chat über den gesamten Lehrinhalt, wahlweise gefiltert auf einen bestimmten Prediger.',
  },
  {
    icon: Search,
    title: 'Vers-Finder',
    text: 'Freitext-Suche nach Bibelstellen — auch ohne exakte Referenz, per KI-Vorschlag mit kurzer Begründung.',
  },
  {
    icon: Headphones,
    title: 'Hören',
    text: 'Die Bibel als Audioerlebnis, ergänzt um passende Predigtausschnitte.',
  },
];

function AboutPage() {
  return (
    <div className="about-page">
      <div className="about-container">

        <section className="about-hero">
          <div className="about-hero-badge">
            <FlaskConical size={14} />
            <span>Lo-Fi Prototyp</span>
          </div>
          <h1>Was ist die Video-Studienbibel?</h1>
          <p className="about-hero-lead">
            Ein Experiment, das Predigtvideos aus der BibelTV Bibelthek mit dem
            Bibeltext selbst verknüpft — Vers für Vers durchsuchbar, statt
            stundenlanger Videos ohne Register. Ausgangspunkt ist aktuell
            Genesis&nbsp;1; die Idee ist als Ansatz für die ganze Bibel gedacht.
          </p>
          <p className="about-hero-note">
            Das hier ist bewusst ein grober, schnell gebauter Prototyp — kein
            fertiges Produkt. Es geht darum, mit echten Videos und echtem
            Bibeltext zu testen, ob die Idee trägt, bevor Zeit in Feinschliff
            investiert wird.
          </p>
        </section>

        <section className="about-section">
          <h2>Features</h2>
          <div className="about-feature-grid">
            {FEATURES.map((f) => (
              <div className="about-feature-card" key={f.title}>
                <div className="about-feature-icon"><f.icon size={20} /></div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="about-section">
          <h2><Rocket size={20} /> Vision &amp; Möglichkeit</h2>
          <p>
            Prediger:innen bringen über Jahre hinweg enormes theologisches
            Wissen in Videoform — aber es verschwindet in unsortierten
            Video-Archiven. Kaum jemand schaut sich eine 45-minütige Predigt
            noch einmal an, um eine bestimmte Stelle wiederzufinden.
          </p>
          <p>
            Die Idee: Was, wenn jeder Bibelvers automatisch mit allen Momenten
            verknüpft wäre, in denen je darüber gepredigt wurde? Eine
            Studienbibel, die nicht nur von Gelehrten vor Jahrhunderten
            geschrieben wurde, sondern sich aus der lebendigen, aktuellen
            Verkündigung einer ganzen Kirche speist — durchsuchbar, filterbar
            nach Prediger, und im Gespräch befragbar.
          </p>
          <p>
            Skaliert auf die komplette Bibelthek und weitere Quellen entstünde
            so ein wachsendes, community-gespeistes Kommentarwerk — ergänzend
            zu klassischen Kommentaren, aber gespeist aus gesprochener,
            gelebter Verkündigung statt nur aus Büchern.
          </p>
        </section>

        <section className="about-section">
          <h2><Cpu size={20} /> Wie es technisch funktioniert</h2>
          <div className="about-pipeline">
            <div className="about-pipeline-step">
              <span className="about-pipeline-num">1</span>
              <div>
                <h4>Videos &amp; Transkription</h4>
                <p>Predigtvideos werden heruntergeladen und per Spracherkennung (Whisper) transkribiert.</p>
              </div>
            </div>
            <div className="about-pipeline-step">
              <span className="about-pipeline-num">2</span>
              <div>
                <h4>KI-Analyse</h4>
                <p>Claude (Anthropic) liest jedes Transkript, erkennt Vers-Referenzen, Lehrmomente, Kategorien und Querverweise.</p>
              </div>
            </div>
            <div className="about-pipeline-step">
              <span className="about-pipeline-num">3</span>
              <div>
                <h4>Datenbank-Aufbau</h4>
                <p>Alle Videos werden nach Vers indexiert, Kommentare synthetisiert, Themen aus Häufungen abgeleitet — Ergebnis ist eine statische JSON-Datenbank.</p>
              </div>
            </div>
            <div className="about-pipeline-step">
              <span className="about-pipeline-num">4</span>
              <div>
                <h4>Clip-Extraktion</h4>
                <p>Für jede Verserwähnung analysiert Claude das umgebende Zeitfenster und bestimmt Start-/Endpunkt, Titel und Kurzbeschreibung des Clips.</p>
              </div>
            </div>
            <div className="about-pipeline-step">
              <span className="about-pipeline-num">5</span>
              <div>
                <h4>React-App</h4>
                <p>Die App lädt die fertige Datenbank beim Start — kein eigenes Backend nötig. Der KI-Chat läuft direkt im Browser gegen die Claude API.</p>
              </div>
            </div>
          </div>
          <div className="about-stack">
            <Database size={16} />
            <span>React 18 · statische JSON-Datenbank · Claude API (Anthropic) · imgix CDN für Thumbnails</span>
          </div>
        </section>

        <section className="about-section about-caveat">
          <h2><Wand2 size={20} /> Grenzen dieses Prototyps</h2>
          <ul>
            <li>Aktuell nur Genesis 1 mit vollständiger Datenbasis abgedeckt.</li>
            <li>KI-generierte Inhalte (Kommentare, Titel, Querverweise) können ungenau sein und ersetzen kein theologisches Studium.</li>
            <li>Design und Bedienung sind funktional, aber nicht final poliert.</li>
            <li>Keine Nutzerkonten, keine Synchronisation zwischen Geräten — Notizen &amp; Highlights sind Demo-Daten.</li>
          </ul>
        </section>

      </div>
    </div>
  );
}

export default AboutPage;
